/**
 * The minimum-shape `User` slice used by the nav-section visibility rule.
 * Modelled as a structural type rather than the full `User` class so the
 * function is callable from non-Web contexts (CLI smoke tests, server-side
 * tools) without dragging the User class along.
 */
export interface NavUser {
	id?: string;
	isAdmin?: boolean;
	isDeveloper?: boolean;
}

/**
 * Visibility flags for each top-level navigation section. The view (and its
 * vm) is responsible for interleaving dividers and rendering the actual
 * link components — this domain function answers only "should the user see
 * this *section*."
 */
export interface NavSectionVisibility {
	/** Show the primary user nav (Dashboard, Submit Jobs, ...) when logged in;
	 *  show the single Login link otherwise. */
	isLoggedIn: boolean;
	/** Show the admin section + its leading divider. */
	showAdmin: boolean;
	/** Show the developer section + its leading divider. */
	showDeveloper: boolean;
	/** Show the standalone Logout entry just below the primary nav. */
	showLogout: boolean;
}

/**
 * Pure visibility rule: given a (possibly-absent) user, decide which of the
 * top-level sidebar sections should appear.
 *
 *  - Logged-in == truthy `id`. (`undefined` user, or user with falsy id,
 *    is the logged-out case — only the Login link shows.)
 *  - Admin section appears when `isAdmin` is truthy *regardless* of
 *    `isLoggedIn` — the existing User class makes `isAdmin` only true for
 *    real users so this is correct in practice; preserving the original
 *    behavior literally rather than adding a redundant `isLoggedIn &&` gate.
 *  - Developer section is independent of admin (a dev who is not also an
 *    admin sees only the developer section, not admin).
 *  - Logout shows iff logged-in.
 *
 * Pure: no React, no router, no IO.
 */
export function visibleNavSections(user: NavUser | undefined): NavSectionVisibility {
	const isLoggedIn = !!user?.id;
	return {
		isLoggedIn,
		showAdmin: !!user?.isAdmin,
		showDeveloper: !!user?.isDeveloper,
		showLogout: isLoggedIn,
	};
}
